import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type {
  CustomFieldDefInput,
  CustomFieldValueUpsertInput,
} from './types';

export const customFieldKeys = {
  all: ['custom-fields'] as const,
  defs: (entity: string) => [...customFieldKeys.all, 'defs', entity] as const,
  values: (entity: string, entityId: string | number) =>
    [...customFieldKeys.all, 'values', entity, String(entityId)] as const,
  contentType: (modelLabel: string) =>
    [...customFieldKeys.all, 'content-type', modelLabel] as const,
};

/** Defs for one entity model ("crm.deal"). */
export function useCustomFieldDefs(entity: string) {
  return useQuery({
    queryKey: customFieldKeys.defs(entity),
    queryFn: () => api.listDefs(entity),
    enabled: Boolean(entity),
  });
}

export function useCreateCustomFieldDef(entity: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomFieldDefInput) => api.createDef(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customFieldKeys.defs(entity) });
      toast.success('Custom field added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteCustomFieldDef(entity: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteDef(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customFieldKeys.defs(entity) });
      toast.success('Custom field removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/** Values attached to one target. */
export function useCustomFieldValues(entity: string, entityId: string | number) {
  return useQuery({
    queryKey: customFieldKeys.values(entity, entityId),
    queryFn: () => api.listValuesForTarget(entity, entityId),
    enabled: Boolean(entity && entityId),
  });
}

/** Resolves "app_label.model" → ContentType id. Cached so the panel only hits this once. */
export function useContentTypeId(modelLabel: string) {
  return useQuery({
    queryKey: customFieldKeys.contentType(modelLabel),
    queryFn: () => api.resolveContentType(modelLabel),
    enabled: Boolean(modelLabel),
    staleTime: Infinity,
  });
}

export function useUpsertCustomFieldValue(entity: string, entityId: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomFieldValueUpsertInput) => api.upsertValue(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customFieldKeys.values(entity, entityId) });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
